import { IpWebComponent } from '../../web-components/ip-web-component';

const statisticContainerTemplate = document.createElement('template');
statisticContainerTemplate.innerHTML = `
<style>
    :host {
        position: sticky;
        top: 10%;
        z-index: 10;
        height: 0px;
    }
    .statistic-container {
        padding: 1rem;
        background-color: rgba(249, 249, 249, 0.7);
        width: fit-content;
        min-width: 250px;
        max-width: 450px;
        opacity: 0;
        transition: opacity 0.5s;
    }
    .statistic-container.show {
        opacity: 1;
    }
    .statistic-container > slot {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    @media(max-width: 768px) {
        :host {
            top: 5%;
        }
    }

    @media(max-width: 576px) {
        :host {
            top: 30px;
        }
        .statistic-container {
            max-width: 100%;
            width: 100%;
        }
    }
</style>
<div class="statistic-container">
</div>
`;

class StatisticContainer extends IpWebComponent {
    private containerElm: HTMLElement | null = null;
    private statisticElms: HTMLElement[] = [];

    constructor() {
        super(statisticContainerTemplate);
    }

    // Init component.
    connectedCallback() {
        this.containerElm = this.queryUnsafe<HTMLElement>('.statistic-container');

        const statisticIcons = [
            this.getProp('statistic1-icon'),
            this.getProp('statistic2-icon'),
            this.getProp('statistic3-icon')
        ];
        const statisticNames = [
            this.getProp('statistic1-name'),
            this.getProp('statistic2-name'),
            this.getProp('statistic3-name')
        ];
        const statisticPostfixes = [
            this.getProp('statistic1-postfix'),
            this.getProp('statistic2-postfix'),
            this.getProp('statistic3-postfix')
        ];

        for (let i = 0; i < statisticNames.length; i++) {
            const name = statisticNames[i];
            if (name) {
                const statElm = document.createElement('statistic-value');
                statElm.setAttribute('icon', statisticIcons[i] || '');
                statElm.setAttribute('name', statisticNames[i] || '');
                statElm.setAttribute('postfix', statisticPostfixes[i] || '');
                this.containerElm.appendChild(statElm);
                this.statisticElms.push(statElm);
            }
        }

        // Listen for when new statistic values are reached.
        this.setupValuesChangeListener();

        // Set when to display the container based on when the first sibling is close to the top.
        const sibling = this.nextElementSibling;
        if (sibling) {
            window.addEventListener('scroll', () => {
                const rect = sibling.getBoundingClientRect();
                const distanceToTop = rect.top;
                this.setShow(distanceToTop < 200);
            });
        }
    }

    static get observedAttributes() {
        return ['show'];
    }

    attributeChangedCallback(name, _oldValue, newValue) {
        if (name === 'show') {
            this.setShow(newValue === 'true');
        }
    }

    private setShow(show: boolean) {
        if (show) {
            this.containerElm?.classList.add('show');
        } else {
            this.containerElm?.classList.remove('show');
        }
    }

    private setupValuesChangeListener() {
        const setStatisticValuesFromElm = (statisticValuesElm?: HTMLElement) => {
            if (!statisticValuesElm) {
                return;
            }
            const statistic1Value = statisticValuesElm.getAttribute('statistic1-value');
            const statistic2Value = statisticValuesElm.getAttribute('statistic2-value');
            const statistic3Value = statisticValuesElm.getAttribute('statistic3-value');
            if (statistic1Value) {
                this.statisticElms[0]?.setAttribute('value', statistic1Value.toString());
            }
            if (statistic2Value) {
                this.statisticElms[1]?.setAttribute('value', statistic2Value.toString());
            }
            if (statistic3Value) {
                this.statisticElms[2]?.setAttribute('value', statistic3Value.toString());
            }
        };

        const getTopmostVisibleEntry = (valueElms: HTMLElement[]) => {
            const filteredEntries = valueElms.filter(
                (e) => !!e.nextElementSibling && e.nextElementSibling.getBoundingClientRect().bottom >= 200
            );

            return filteredEntries[0];
        };

        // Find all value elements and set up scroll listener.

        const valueElms = [...document.querySelectorAll('.statistic-values')] as HTMLElement[];
        // Only include value elements that have at least one value set.
        const validValueElms = valueElms.filter(
            (elm) =>
                !!elm.getAttribute('statistic1-value') ||
                !!elm.getAttribute('statistic2-value') ||
                !!elm.getAttribute('statistic3-value')
        );
        window.addEventListener('scroll', () => {
            const valuesElm = getTopmostVisibleEntry(validValueElms);
            // Use visible element that is valid or pick the last element (if all elements are outside of top of view).
            setStatisticValuesFromElm(valuesElm || validValueElms[validValueElms.length - 1]);
        });

        // Set initial values.
        const valuesElm = getTopmostVisibleEntry(validValueElms);
        // Use visible element that is valid or pick the last element (if all elements are outside of top of view).
        setStatisticValuesFromElm(valuesElm || validValueElms[validValueElms.length - 1]);
    }
}

// Define element, if not already defined.
if (customElements.get('statistic-container') === undefined) {
    customElements.define('statistic-container', StatisticContainer);
}
